import * as React from 'react'
import produce from 'immer'
import { Form, Spin, Button, Tooltip, Tabs, Input } from 'antd'
import { isNil, set, camelCase, get } from 'lodash'
import { showError, resourceNotFound, ValidationError } from 'helpers/errors'
import { createLabelFactory, createFieldFactory, createHandleSubmit, isReadOnly } from 'helpers/formViews'
import alignments from 'options/labelTemplates/alignments'
import fontNames from 'options/fontNames'
import { t } from 'helpers/i18n'
import { stopEvent } from 'helpers/events'
import { formatDateTime } from 'helpers/dateTime'
import Select, { Option, getOptionProps } from 'elements/Select'
import InputNumber from 'elements/InputNumber'
import Checkbox from 'elements/Checkbox'
import { Row, Col } from 'elements/Grid'
import InputImage from 'elements/InputImage'
import Drawer from 'elements/Drawer'
import FileUpload from 'elements/FileUpload'

class FormView extends React.Component {
  state = {}

  constructor(props) {
    super(props)

    this.handleSubmit = createHandleSubmit(this)
  }

  componentDidMount() {
    this.fetchItem()
  }

  getItemId = () => [this.state.item?.id, this.props.linkTargetRecord?.id].find(Boolean)

  fetchItem = async ({ itemId = this.getItemId() } = {}) => {
    try {
      const { rootRecord, parentRecord } = this.props
      const responses = await Promise.all([
        itemId
          ? this.props.getItem(itemId).catch((error) => {
              this.props.onCancel?.()
              throw error
            })
          : !this.props.linkTargetRecord
            ? this.props.newItem({
                alignment: 'Left',
                lineId: parentRecord.id,
                position: rootRecord.lines.find((one) => one.id === parentRecord.id).fields.length + 1,
                width: rootRecord.width - rootRecord.leftMargin - rootRecord.rightMargin,
                topMargin: 0,
                bottomMargin: 0,
                leftMargin: 0,
                rightMargin: 0,
                borderWidth: 0.01,
                fontName: '',
                fontSize: 12,
                previewText: '',
                customText: '',
              })
            : resourceNotFound(this),
        this.props.getSettings({ type: 'field' }),
        this.props.getLabelTemplateFields({ type: camelCase(rootRecord.labelTemplateType) }),
      ])

      this.setState({
        item: responses[0].value.data,
        fieldSettings: responses[1].value.data.fieldSettings,
        labelTemplateFields: get(responses[2], 'value.data.items', []),
      })
    } catch (error) {
      showError({ error })
    }
  }

  setItemValue = (name, value) =>
    this.setState(
      produce((draft) => {
        set(draft.item, name, value)

        if (name === 'fieldType') {
          draft.item.previewText = this.getFieldTypeName(value)
        }

        this.props.form.setFieldsValue({ [name]: value })
      })
    )

  handleFileUpload = (fileList = []) => {
    if (fileList[0]) {
      this.setState(
        produce((draft) => {
          draft.item.image = {
            contents: fileList[0].fileContents,
            fileType: fileList[0].fileType,
          }
          draft.fileUploadDrawerVisible = false
        })
      )
    } else {
      this.setState({ fileUploadDrawerVisible: false })
    }
  }

  getFieldTypeName = (fieldType) => {
    try {
      return this.state.labelTemplateFields.find((one) => one.fieldType === fieldType).fieldTypeName
    } catch (error) {
      console.warn(error)
      return t(fieldType)
    }
  }

  render() {
    const { rootRecord } = this.props
    const { item, fieldSettings } = this.state

    if (!item || !fieldSettings) {
      return <Spin />
    }

    const createLabel = createLabelFactory(fieldSettings)
    const createFieldDecorator = createFieldFactory(this.props.form, item, fieldSettings)
    const readOnly = isReadOnly(this)

    const siblingRecords = rootRecord.lines.find((one) => one.id === item.lineId).fields

    const unitProps = {
      precision: 2,
      step: 0.01,
      formatter: (value) => `${value} ${rootRecord.unitType === 'Centimeters' ? 'cm' : 'in'}`,
      parser: (value) => value.replace(/[cmin\s]/g, ''),
    }

    // console.log({ props: this.props, state: this.state })

    return (
      <>
        <Form layout="vertical" colon={false} onSubmit={readOnly ? stopEvent : this.handleSubmit}>
          <div className="form-items-container">
            <ValidationError errors={this.state.validationErrors} />
            <Tabs defaultActiveKey={item.id ? 'appearance' : 'field'}>
              <Tabs.TabPane key="field" tab={t('field')} forceRender>
                <Form.Item label={createLabel('fieldType')}>
                  {createFieldDecorator('fieldType')(
                    <Select
                      onChange={(value) => this.setItemValue('fieldType', value)}
                      placeholder={t('none')}
                      allowClear={false}
                      readOnly={readOnly}
                      disabled={Boolean(item.id)}
                    >
                      {(this.state.labelTemplateFields ?? []).map((each) => (
                        <Option key={each.fieldType} value={each.fieldType}>
                          {each.fieldTypeName}
                        </Option>
                      ))}
                    </Select>
                  )}
                </Form.Item>
                <Row>
                  <Col xs={12}>
                    <Form.Item label={createLabel('lineId')}>
                      {createFieldDecorator('lineId')(
                        <Select
                          onChange={(value) => this.setItemValue('lineId', value)}
                          placeholder={t('none')}
                          allowClear={false}
                          readOnly={readOnly}
                        >
                          {rootRecord.lines.map((each) => (
                            <Option key={each.id} value={each.id}>
                              <span {...getOptionProps(each)}>{`${t('line')} ${each.number}`}</span>
                            </Option>
                          ))}
                        </Select>
                      )}
                    </Form.Item>
                  </Col>
                  <Col xs={12}>
                    <Form.Item label={createLabel('position')}>
                      {createFieldDecorator('position')(
                        <InputNumber
                          min={1}
                          max={isNil(item.id) ? siblingRecords.length + 1 : siblingRecords.length}
                          readOnly={readOnly}
                        />
                      )}
                    </Form.Item>
                  </Col>
                </Row>
                {this.props.form.getFieldValue('fieldType') === 'Image' && (
                  <InputImage
                    label={t('image')}
                    onUpload={() => this.setState({ fileUploadDrawerVisible: true })}
                    onDelete={() =>
                      this.setState(
                        produce((draft) => {
                          draft.item.image = null
                        })
                      )
                    }
                    value={this.state.item.image}
                    readOnly={readOnly}
                  />
                )}
                {this.props.form.getFieldValue('fieldType') === 'CustomText' && (
                  <Form.Item label={createLabel('customText')}>
                    {createFieldDecorator('customText')(<Input.TextArea rows={4} readOnly={readOnly} />)}
                  </Form.Item>
                )}
                {!['Image', 'AssetImage', 'ProductImage', 'CustomText'].includes(
                  this.props.form.getFieldValue('fieldType')
                ) && (
                  <Form.Item label={createLabel('previewText')}>
                    <Input.TextArea
                      value={
                        item.fieldType === 'InventoryLastReceivedDate'
                          ? formatDateTime(new Date(), { showDate: true, showTime: true })
                          : item.previewText
                      }
                      onChange={(e) => this.setItemValue('previewText', e.target.value)}
                      rows={4}
                      readOnly={readOnly}
                      disabled={item.fieldType === 'InventoryLastReceivedDate'}
                    />
                  </Form.Item>
                )}
                {this.props.form.getFieldValue('fieldType') &&
                  !this.props.form.getFieldValue('fieldType').match(/Image|QR/) && (
                    <Row>
                      <Col xs={12}>
                        <Form.Item label={createLabel('preamble')}>
                          {createFieldDecorator('preamble')(<Input readOnly={readOnly} />)}
                        </Form.Item>
                      </Col>
                      <Col xs={12}>
                        <Form.Item label={createLabel('postamble')}>
                          {createFieldDecorator('postamble')(<Input readOnly={readOnly} />)}
                        </Form.Item>
                      </Col>
                    </Row>
                  )}
              </Tabs.TabPane>
              <Tabs.TabPane key="appearance" tab={t('appearance')} forceRender>
                <Row>
                  <Col xs={12}>
                    <Form.Item label={createLabel('width')}>
                      {createFieldDecorator('width')(
                        <InputNumber min={0} readOnly={readOnly} {...unitProps} />
                      )}
                    </Form.Item>
                  </Col>
                  <Col xs={12}>
                    <Form.Item label={createLabel('alignment')}>
                      {createFieldDecorator('alignment')(
                        <Select allowClear={false} readOnly={readOnly}>
                          {Object.entries(alignments).map(([key, value]) => (
                            <Option key={key} value={key}>
                              {t(value)}
                            </Option>
                          ))}
                        </Select>
                      )}
                    </Form.Item>
                  </Col>
                </Row>
                <Row>
                  <Col xs={12}>
                    <Form.Item label={createLabel('topMargin')}>
                      {createFieldDecorator('topMargin')(
                        <InputNumber min={0} readOnly={readOnly} {...unitProps} />
                      )}
                    </Form.Item>
                  </Col>
                  <Col xs={12}>
                    <Form.Item label={createLabel('bottomMargin')}>
                      {createFieldDecorator('bottomMargin')(
                        <InputNumber min={0} readOnly={readOnly} {...unitProps} />
                      )}
                    </Form.Item>
                  </Col>
                </Row>
                <Row>
                  <Col xs={12}>
                    <Form.Item label={createLabel('leftMargin')}>
                      {createFieldDecorator('leftMargin')(
                        <InputNumber min={0} readOnly={readOnly} {...unitProps} />
                      )}
                    </Form.Item>
                  </Col>
                  <Col xs={12}>
                    <Form.Item label={createLabel('rightMargin')}>
                      {createFieldDecorator('rightMargin')(
                        <InputNumber min={0} readOnly={readOnly} {...unitProps} />
                      )}
                    </Form.Item>
                  </Col>
                </Row>
                {this.props.form.getFieldValue('fieldType') &&
                !this.props.form.getFieldValue('fieldType').match(/Image|QR/) ? (
                  <>
                    <Row>
                      <Col xs={12}>
                        <Form.Item label={createLabel('fontName')}>
                          {createFieldDecorator('fontName')(
                            <Select placeholder={t('default')} allowClear={false} readOnly={readOnly}>
                              <Option key="" value="">
                                {t('default')}
                              </Option>
                              {fontNames.map((each) => (
                                <Option key={each} value={each}>
                                  <span style={{ fontFamily: each }}>{each}</span>
                                </Option>
                              ))}
                            </Select>
                          )}
                        </Form.Item>
                      </Col>
                      <Col xs={12}>
                        <Form.Item label={createLabel('fontSize')}>
                          {createFieldDecorator('fontSize')(<InputNumber min={8} readOnly={readOnly} />)}
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row>
                      <Col xs={12}>
                        <Form.Item>
                          {createFieldDecorator('bold', { valuePropName: 'checked' })(
                            <Checkbox readOnly={readOnly}>{createLabel('bold')}</Checkbox>
                          )}
                        </Form.Item>
                      </Col>
                      <Col xs={12}>
                        <Form.Item>
                          {createFieldDecorator('underline', { valuePropName: 'checked' })(
                            <Checkbox readOnly={readOnly}>{createLabel('underline')}</Checkbox>
                          )}
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row>
                      <Col xs={12}>
                        <Form.Item>
                          {createFieldDecorator('italic', { valuePropName: 'checked' })(
                            <Checkbox readOnly={readOnly}>{createLabel('italic')}</Checkbox>
                          )}
                        </Form.Item>
                      </Col>
                      <Col xs={12}>
                        <Form.Item>
                          {createFieldDecorator('wrapText', { valuePropName: 'checked' })(
                            <Checkbox readOnly={readOnly}>{createLabel('wrapText')}</Checkbox>
                          )}
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row>
                      <Col xs={12}>
                        <Form.Item>
                          {createFieldDecorator('showBorder', { valuePropName: 'checked' })(
                            <Checkbox readOnly={readOnly}>{createLabel('showBorder')}</Checkbox>
                          )}
                        </Form.Item>
                      </Col>
                      <Col xs={12}>
                        <Form.Item>
                          {createFieldDecorator('borderWidth')(
                            <InputNumber
                              min={0.01}
                              readOnly={readOnly}
                              {...unitProps}
                              disabled={!this.props.form.getFieldValue('showBorder')}
                            />
                          )}
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row>
                      <Col xs={12}>
                        <Form.Item>
                          {createFieldDecorator('showAsNegative', { valuePropName: 'checked' })(
                            <Checkbox readOnly={readOnly}>{createLabel('showAsNegative')}</Checkbox>
                          )}
                        </Form.Item>
                      </Col>
                    </Row>
                  </>
                ) : (
                  <>
                    <Row>
                      <Col xs={12}>
                        <Form.Item>
                          {createFieldDecorator('showBorder', { valuePropName: 'checked' })(
                            <Checkbox readOnly={readOnly}>{createLabel('showBorder')}</Checkbox>
                          )}
                        </Form.Item>
                      </Col>
                      <Col xs={12}>
                        <Form.Item>
                          {createFieldDecorator('borderWidth')(
                            <InputNumber
                              min={0.01}
                              readOnly={readOnly}
                              {...unitProps}
                              disabled={!this.props.form.getFieldValue('showBorder')}
                            />
                          )}
                        </Form.Item>
                      </Col>
                    </Row>
                  </>
                )}
              </Tabs.TabPane>
            </Tabs>
          </div>
          <div className="form-buttons-container">
            <Button onClick={this.props.onCancel}>{t(readOnly ? 'close' : 'cancel')}</Button>{' '}
            {!readOnly && (
              <Tooltip title={t('childRecordSaveInfo')} placement="topLeft">
                <Button type="primary" htmlType="submit" loading={this.state.saveAndCloseButtonLoading}>
                  {!this.props.linkTargetRecord ? t('add') : t('update')}
                </Button>
              </Tooltip>
            )}
          </div>
        </Form>
        <Drawer
          title={t('selectFile')}
          size="xs"
          visible={this.state.fileUploadDrawerVisible}
          onClose={() => this.setState({ fileUploadDrawerVisible: false })}
        >
          <FileUpload
            accept=".jpg,.jpeg,.png,.gif,image/jpeg,image/png,image/gif"
            onUpload={this.handleFileUpload}
            onCancel={() => this.setState({ fileUploadDrawerVisible: false })}
          />
        </Drawer>
      </>
    )
  }
}

export default Form.create()(FormView)
